import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import StudentImportModal from '../components/StudentImportModal';

const STATUS_OPTIONS = [
  { value: 'E', label: '재학' },
  { value: 'G', label: '졸업' },
  { value: 'D', label: '퇴학' },
  { value: 'T', label: '전학' },
];

function statusLabel(v) {
  return STATUS_OPTIONS.find((o) => o.value === v)?.label || v;
}

function emptyForm() {
  return {
    studid: '',
    grade: 1,
    class_no: '',
    studno: '',
    name: '',
    mclass: '',
    mstudno: '',
  };
}

export default function StudentManagement() {
  const [rows, setRows] = useState([]);
  const [classtypeFilter, setClasstypeFilter] = useState('M');
  const [gradeFilter, setGradeFilter] = useState('');
  const [classOptions, setClassOptions] = useState([]);
  const [classFilter, setClassFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('E');
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [message, setMessage] = useState('');
  const [importMessage, setImportMessage] = useState('');
  const [importModalOpen, setImportModalOpen] = useState(false);

  // 학년/반구분이 바뀌면 그에 맞는 반 목록을 다시 불러오고, 반 선택은 초기화한다
  useEffect(() => {
    setClassFilter('');
    if (gradeFilter === '') {
      setClassOptions([]);
      return;
    }
    api
      .get('/classes', { params: { grade: gradeFilter, classtype: classtypeFilter } })
      .then((res) => setClassOptions(res.data));
  }, [gradeFilter, classtypeFilter]);

  const loadRows = useCallback(async () => {
    // 학년을 선택하기 전에는 조회하지 않는다 (페이지가 열리자마자 전체 학생이 표시되지 않도록)
    if (gradeFilter === '') {
      setRows([]);
      return;
    }
    const params = { classtype: classtypeFilter, grade: gradeFilter };
    if (classFilter !== '') params.class_no = classFilter;
    if (statusFilter !== '') params.status_ = statusFilter;
    const res = await api.get('/admin/student-management', { params });
    setRows(res.data);
  }, [classtypeFilter, gradeFilter, classFilter, statusFilter]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormOpen(true);
    setMessage('');
  };

  const openEdit = (row) => {
    setEditingId(row.studid);
    setForm({
      studid: row.studid,
      grade: row.grade,
      class_no: row.class_no,
      studno: row.studno ?? '',
      name: row.name,
      mclass: row.mclass ?? '',
      mstudno: row.mstudno ?? '',
    });
    setFormOpen(true);
    setMessage('');
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (form.class_no === '' || !form.name.trim()) {
      setMessage('반과 이름을 입력하세요.');
      return;
    }
    const body = {
      grade: Number(form.grade),
      class_no: Number(form.class_no),
      studno: form.studno === '' ? null : Number(form.studno),
      name: form.name.trim(),
      mclass: form.mclass === '' ? null : Number(form.mclass),
      mstudno: form.mstudno === '' ? null : Number(form.mstudno),
    };
    try {
      if (editingId) {
        await api.put(`/admin/student-management/${editingId}`, body);
      } else {
        await api.post('/admin/student-management', { studid: form.studid, ...body });
      }
      closeForm();
      await loadRows();
    } catch (err) {
      setMessage(err.response?.data?.detail || '저장에 실패했습니다.');
    }
  };

  const changeStatus = async (row, nextStatus) => {
    const verb = statusLabel(nextStatus);
    if (!window.confirm(`${row.name} 학생을 "${verb}" 상태로 변경하시겠습니까?`)) return;
    await api.put(`/admin/student-management/${row.studid}/status`, { status: nextStatus });
    await loadRows();
  };

  const handleImported = async ({ created, skipped }) => {
    setImportMessage(`${created}명 등록, ${skipped}명 건너뜀(이미 등록됨)`);
    setImportModalOpen(false);
    await loadRows();
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>학생관리</h2>
      </div>

      <div className="card">
        <div className="board-header">
          <h3>학생 목록</h3>
          <div className="comment-actions">
            <button className="btn-ghost" type="button" onClick={() => setImportModalOpen(true)}>
              신입생 일괄 등록(엑셀)
            </button>
            {!formOpen && (
              <button className="btn-primary btn-inline" onClick={openCreate}>
                + 학생 개별 등록
              </button>
            )}
          </div>
        </div>

        {importMessage && <div className="form-message">{importMessage}</div>}

        <div className="form-row">
          <div className="form-field form-field-narrow">
            <label>반구분</label>
            <select value={classtypeFilter} onChange={(e) => setClasstypeFilter(e.target.value)}>
              <option value="R">정규반</option>
              <option value="M">면장반</option>
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>학년</label>
            <select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)}>
              <option value="">전체</option>
              <option value={1}>1학년</option>
              <option value={2}>2학년</option>
              <option value={3}>3학년</option>
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>반</label>
            <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
              <option value="">전체</option>
              {classOptions.map((c) => (
                <option key={c} value={c}>{c}반</option>
              ))}
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>상태</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">전체</option>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {formOpen && (
          <form className="board-write-form" onSubmit={submitForm}>
            <div className="form-row">
              <div className="form-field form-field-narrow">
                <label>학번</label>
                <input
                  className="field-input"
                  type="text"
                  value={form.studid}
                  disabled={!!editingId}
                  onChange={(e) => updateField('studid', e.target.value)}
                />
              </div>
              <div className="form-field form-field-narrow">
                <label>이름</label>
                <input
                  className="field-input"
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                />
              </div>
              <div className="form-field form-field-narrow">
                <label>학년</label>
                <select value={form.grade} onChange={(e) => updateField('grade', e.target.value)}>
                  <option value={1}>1학년</option>
                  <option value={2}>2학년</option>
                  <option value={3}>3학년</option>
                </select>
              </div>
              <div className="form-field form-field-narrow">
                <label>반</label>
                <input
                  className="field-input"
                  type="number"
                  min="1"
                  value={form.class_no}
                  onChange={(e) => updateField('class_no', e.target.value)}
                />
              </div>
              <div className="form-field form-field-narrow">
                <label>번호</label>
                <input
                  className="field-input"
                  type="number"
                  min="1"
                  value={form.studno}
                  onChange={(e) => updateField('studno', e.target.value)}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field form-field-narrow">
                <label>면장반</label>
                <input
                  className="field-input"
                  type="number"
                  min="1"
                  value={form.mclass}
                  onChange={(e) => updateField('mclass', e.target.value)}
                  placeholder="해당 시"
                />
              </div>
              <div className="form-field form-field-narrow">
                <label>면장번호</label>
                <input
                  className="field-input"
                  type="number"
                  min="1"
                  value={form.mstudno}
                  onChange={(e) => updateField('mstudno', e.target.value)}
                  placeholder="해당 시"
                />
              </div>
            </div>

            {message && <div className="form-message">{message}</div>}

            <div className="comment-actions">
              <button className="btn-primary btn-inline" type="submit">
                {editingId ? '수정 저장' : '등록'}
              </button>
              <button className="btn-ghost" type="button" onClick={closeForm}>취소</button>
            </div>
          </form>
        )}

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>상태</th>
                <th>학번</th>
                <th>이름</th>
                <th>학년</th>
                <th>반</th>
                <th>번호</th>
                <th>면장반</th>
                <th>면장번호</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.studid}>
                  <td>{statusLabel(r.status)}</td>
                  <td>{r.studid}</td>
                  <td>{r.name}</td>
                  <td>{r.grade}학년</td>
                  <td>{r.class_no}반</td>
                  <td>{r.studno ?? ''}</td>
                  <td>{r.mclass ?? ''}</td>
                  <td>{r.mstudno ?? ''}</td>
                  <td className="board-title-cell">
                    <button className="btn-link" onClick={() => openEdit(r)}>수정</button>
                    {' '}
                    {r.status === 'E' ? (
                      <>
                        <button className="btn-link" onClick={() => changeStatus(r, 'D')}>퇴학</button>
                        {' '}
                        <button className="btn-link" onClick={() => changeStatus(r, 'T')}>전학</button>
                      </>
                    ) : (
                      <button className="btn-link" onClick={() => changeStatus(r, 'E')}>재학 복귀</button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan="9" className="empty-row">
                    {gradeFilter === '' ? '학년을 선택하면 학생 목록이 표시됩니다.' : '해당 조건의 학생이 없습니다.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {importModalOpen && (
        <StudentImportModal
          onClose={() => setImportModalOpen(false)}
          onImported={handleImported}
        />
      )}
    </div>
  );
}
